import React, { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IExamMasterCodeItem } from '../../../api/user/model';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../models';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { SelectChangeEvent } from '@mui/material';

export interface UsePrCitizenshipFormProps {
  formHook: UseFormReturn;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  profileData?: CandidateProfileFormData;
  isSingPassUser?: boolean;
  isIExamsApiEnabled?: boolean;
}

type masterCodeFieldType = IExamMasterCodeItem | null;

export interface UsePrCitizenshipFormReturn {
  /**
   * citizenship state
   */
  citizenshipState: masterCodeFieldType;
  /**
   * set citizenship state
   */
  setCitizenship: React.Dispatch<React.SetStateAction<masterCodeFieldType>>;
  /**
   * Callback method when pr status value changed
   */
  onPrStatusChange: (e: React.ChangeEvent<{}>, prValue: any) => void;
  /**
   * Callback method when candidate id value changed
   */
  onIdChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  /**
   * Callback method when id type value changed
   */
  onIdTypeChange: (event: SelectChangeEvent<unknown>, child: ReactNode) => void;
  /**
   * pr status state
   */
  prStatusState: masterCodeFieldType;
  /**
   * pr status option
   */
  prStatusOption: IExamMasterCodeItem[];
  /**
   * Indicator whether it is foreigner setting based on the id type and candidate id.
   */
  isForeignerSetting: boolean;
}

const SINGAPORE_CITIZEN = 'SINGAPORE CITIZEN';
const SINGAPORE_PR = 'SINGAPORE PERMANENT RESIDENTS';
const INTERNATIONAL_STUDENTS = 'INTERNATIONAL STUDENTS';
const ALIEN = 'ALIEN';
const FOREIGNER = 'FOREIGNER';

/**
 * Get target citizenship master code based on the pr status.
 * Return INTERNATIONAL_STUDENTS if the pr status is not SINGAPORE_CITIZEN or SINGAPORE_PR.
 *
 * @param prValue pr status value
 * @param citizenshipOption available citizenship master code
 */
export const getTargetCitizenshipCodeItem = (
  prValue: string,
  citizenshipOption: IExamMasterCodeItem[] | undefined
): IExamMasterCodeItem | null => {
  const prStatusDesc = prValue.toUpperCase();
  let targetCitizenship = '';
  if (prStatusDesc === SINGAPORE_CITIZEN || prStatusDesc === SINGAPORE_PR) {
    targetCitizenship = prStatusDesc;
  } else {
    targetCitizenship = INTERNATIONAL_STUDENTS;
  }
  return citizenshipOption?.find((val) => val.codeValueDescription === targetCitizenship) || null;
};

/**
 * Get target pr status master code based on the pr status value.
 *
 * @param prStatus target pr status
 * @param prStatusOption available pr status option
 */
export const getTargetPrStatusCodeItem = (
  prStatus: string,
  prStatusOption: IExamMasterCodeItem[] | undefined
): IExamMasterCodeItem | null => {
  const prStatusDesc = prStatus.toUpperCase();
  return prStatusOption?.find((val) => val.codeValueDescription === prStatusDesc) || null;
};

/**
 * Transform the pr status options array to display name.
 * "ALIEN" to "FOREIGNER"
 *
 * @param prStatusOption available pr status option
 */
export const transformPrOptions = (
  prStatusOption: IExamMasterCodeItem[] | undefined
): IExamMasterCodeItem[] =>
  prStatusOption?.map((obj) => {
    if (obj.codeValueDescription === ALIEN) {
      return { ...obj, codeValueDescription: FOREIGNER };
    }
    return obj;
  }) || [];

/**
 * Convert the pr status string to display name.
 * "ALIEN" to "FOREIGNER"
 *
 * @param prStatus pr status
 */
export const convertPrStatusToDisplayName = (prStatus: string | undefined): string => {
  if (!prStatus) {
    return '-';
  }
  if (prStatus.toUpperCase() === ALIEN) {
    return FOREIGNER;
  }
  return prStatus;
};

/**
 * Convert the display name to pr status string.
 * "FOREIGNER" to "ALIEN"
 *
 * @param displayName display name of pr status
 */
export const convertDisplayNameToPrStatus = (
  displayName: string | null | undefined
): string | null => {
  if (!displayName) {
    return null;
  }

  if (displayName.toUpperCase() === FOREIGNER) {
    return ALIEN;
  }
  return displayName;
};

/**
 * Get the pr status option based on isForeigner flag.
 * Return "FOREIGNER" master code if isForeigner flag is true.
 * Else return "SINGAPORE_CITIZEN" and "SINGAPORE_PR" master code.
 *
 * @param isForeigner indicator for target pr status
 * @param prStatusOption available pr status option
 */
export const getPrStatusOption = (
  isForeigner: boolean | null,
  prStatusOption: IExamMasterCodeItem[] | undefined
): IExamMasterCodeItem[] => {
  if (isForeigner === null) {
    return (
      prStatusOption?.filter(
        (val) =>
          val.codeValueDescription === SINGAPORE_CITIZEN ||
          val.codeValueDescription === SINGAPORE_PR ||
          val.codeValueDescription === FOREIGNER
      ) || []
    );
  }

  if (isForeigner) {
    return prStatusOption?.filter((val) => val.codeValueDescription === FOREIGNER) || [];
  }
  return (
    prStatusOption?.filter(
      (val) =>
        val.codeValueDescription === SINGAPORE_CITIZEN || val.codeValueDescription === SINGAPORE_PR
    ) || []
  );
};

/**
 * A hook to handle citizenship auto-selection when pr status changed.
 *
 */
export const usePrCitizenshipForm = ({
  formHook,
  isDisableInputs,
  formDefaultValues,
  profileData,
  isSingPassUser = false,
  isIExamsApiEnabled = true,
}: UsePrCitizenshipFormProps): UsePrCitizenshipFormReturn => {
  const { data: dataIEXAM } = useGetIExamMasterCode({ enabled: isIExamsApiEnabled });

  const getIdType = () => {
    if (profileData) {
      return profileData.IdType;
    }
    return formHook.getValues('IdType');
  };

  const getCandidateId = () => {
    if (profileData) {
      return profileData.NricFin;
    }
    return formHook.getValues('NricFin');
  };

  const [prStatusOption, setPrStatusOption] = useState<IExamMasterCodeItem[]>([]);
  const transformedPrStatusOptions = useMemo<IExamMasterCodeItem[]>(() => {
    const options = transformPrOptions(dataIEXAM?.prStatus);
    setPrStatusOption(getPrStatusOption(getIdType() === 'P', options));
    return options;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [dataIEXAM]);

  const [citizenshipState, setCitizenship] = useState<masterCodeFieldType>(
    formDefaultValues?.citizenship || null
  );

  const [prStatusState, setPrStatus] = useState<masterCodeFieldType>(
    formDefaultValues?.prStatus || null
  );

  const [isForeignerSetting, setIsForeignerSetting] = useState<boolean>(getIdType() === 'P');
  const updateSettingForSingpassUser = () => {
    const prStatusDisabled = isDisableInputs?.prStatus;
    const blankPrStatus = !formDefaultValues?.prStatus;
    if (blankPrStatus && !prStatusDisabled) {
      setPrStatusOption(getPrStatusOption(true, transformedPrStatusOptions));
    } else {
      setPrStatusOption(getPrStatusOption(null, transformedPrStatusOptions));
    }
  };

  // Hook to update the setting after profile data updated. (for self-creation flow)
  useEffect(() => {
    if (isSingPassUser) {
      updateSettingForSingpassUser();
      return;
    }
    if (getIdType() === 'P') {
      updateToForeignerSetting();
    } else {
      const candidateId = getCandidateId();
      updateSettingBasedOnFirstChar(candidateId || '');
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [profileData, isDisableInputs]);

  const updateCitizenshipState = (citizenshipMasterCode: IExamMasterCodeItem | null) => {
    setCitizenship(citizenshipMasterCode);
    formHook.setValue('citizenship', citizenshipMasterCode || undefined);
  };

  const updatePrStatusState = (prStatusMasterCode: IExamMasterCodeItem | null) => {
    setPrStatus(prStatusMasterCode);
    formHook.setValue('prStatus', prStatusMasterCode || undefined);
  };

  const onPrStatusChange = (e: React.ChangeEvent<{}>, prValue: any) => {
    if (isDisableInputs?.citizenship && prValue) {
      const citizenshipCodeItem = getTargetCitizenshipCodeItem(
        prValue.codeValueDescription,
        dataIEXAM?.citizenship
      );
      updateCitizenshipState(citizenshipCodeItem);
      updatePrStatusState(prValue);
    }
  };

  const updateToForeignerSetting = () => {
    setIsForeignerSetting(true);
    setPrStatusOption(getPrStatusOption(true, transformedPrStatusOptions));
    const targetPrStatus = getTargetPrStatusCodeItem(FOREIGNER, transformedPrStatusOptions);
    updatePrStatusState(targetPrStatus);
    if (targetPrStatus) {
      const citizenshipCodeItem = getTargetCitizenshipCodeItem(
        targetPrStatus.codeValueDescription,
        dataIEXAM?.citizenship
      );
      updateCitizenshipState(citizenshipCodeItem);
    }
  };

  const updateToNonForeignerSetting = () => {
    setIsForeignerSetting(false);
    setPrStatusOption(getPrStatusOption(false, transformedPrStatusOptions));
    updatePrStatusState(null);
    updateCitizenshipState(null);
  };

  const updateSettingBasedOnFirstChar = (candidateId: string) => {
    const firstChar = candidateId.toUpperCase().charAt(0);
    // if first char is blank, default set to non-foreigner setting
    if (firstChar === 'S' || firstChar === 'T' || firstChar === '') {
      if (isForeignerSetting) {
        updateToNonForeignerSetting();
      }
    } else {
      updateToForeignerSetting();
    }
  };

  const onIdChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const candidateId = e.target.value;
    const idType = getIdType();
    if (idType === 'P') {
      return;
    }
    updateSettingBasedOnFirstChar(candidateId);
  };

  const onIdTypeChange = (
    event: SelectChangeEvent<unknown>,
    child: ReactNode
  ) => {
    const idType = event.target.value;
    formHook.setValue('IdType', event.target.value);
    if (idType === 'P') {
      updateToForeignerSetting();
    } else {
      const candidateId = getCandidateId();
      updateSettingBasedOnFirstChar(candidateId || '');
    }
  };

  return {
    citizenshipState,
    setCitizenship,
    onPrStatusChange,
    onIdChange,
    onIdTypeChange,
    prStatusOption,
    prStatusState,
    isForeignerSetting,
  };
};
